import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';
import { apiFetch } from '../utils/apiClient';
import { buildTwitchSignInUrl, clearLegacyAuthStorage } from '../utils/authUrls';
import type { ScoutlyticsUser } from '../types/user';

interface UserContextType {
  user: ScoutlyticsUser | null;
  loading: boolean;
  isAuthenticated: boolean;
  authError: string | null;
  refreshUser: () => Promise<void>;
  acceptTerms: () => Promise<void>;
  signInWithTwitch: (returnTo?: string) => void;
  logout: () => Promise<void>;
  clearAuthError: () => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
  children: ReactNode;
}

function defaultReturnTo(): string {
  return window.location.pathname + window.location.search;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<ScoutlyticsUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await apiFetch('/api/me');
      if (response.status === 401) {
        setUser(null);
        return;
      }
      if (!response.ok) {
        setUser(null);
        setAuthError('Could not verify your sign-in status. Please try again.');
        return;
      }
      const data = (await response.json()) as ScoutlyticsUser;
      setUser(data);
      setAuthError(null);
    } catch (error) {
      console.error('Error refreshing user session:', error);
      setUser(null);
      setAuthError('Could not verify your sign-in status. Please try again.');
    }
  }, []);

  useEffect(() => {
    clearLegacyAuthStorage();
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const signInWithTwitch = useCallback((returnTo?: string) => {
    const target = returnTo ?? defaultReturnTo();
    window.location.href = buildTwitchSignInUrl(target);
  }, []);

  const acceptTerms = useCallback(async () => {
    const response = await apiFetch('/api/me/accept-terms', { method: 'POST' });
    if (!response.ok) {
      throw new Error('accept_terms_failed');
    }
    const data = (await response.json()) as ScoutlyticsUser;
    setUser(data);
    setAuthError(null);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/auth/logout', { method: 'POST' });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      setAuthError(null);
    }
  }, []);

  const value: UserContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    authError,
    refreshUser,
    acceptTerms,
    signInWithTwitch,
    logout,
    clearAuthError,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
